import {useCallback, useReducer} from 'react';

interface State<T> {
    data?: T;
    error?: Error;
}

type Action<T> = {type: 'loading'} | {type: 'fetched'; payload: T} | {type: 'error'; payload: Error};

function useMutation<T = unknown>(url: string, options?: RequestInit): [State<T>, (body: BodyInit, query?: URLSearchParams) => void] {
    const initialState: State<T> = {
        error: undefined,
        data: undefined,
    };

    const fetchReducer = (state: State<T>, action: Action<T>): State<T> => {
        switch (action.type) {
            case 'loading':
                return {...initialState};
            case 'fetched':
                return {...initialState, data: action.payload};
            case 'error':
                return {...initialState, error: action.payload};
            default:
                return state;
        }
    };

    const [state, dispatch] = useReducer(fetchReducer, initialState);

    const fetchData = useCallback(
        async (body: BodyInit, query?: URLSearchParams) => {
            dispatch({type: 'loading'});

            try {
                const response = await fetch(query ? `${url}?${query}` : url, {...options, body});

                if (!response.ok) {
                    throw new Error(response.statusText);
                }

                dispatch({type: 'fetched', payload: (await response.json()) as T});
            } catch (error) {
                dispatch({type: 'error', payload: error as Error});
            }
        },
        [options, url],
    );

    return [state, fetchData];
}

export default useMutation;
